import { ArrowUp, ArrowDown, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

// Generic 列設定 dialog reused by any table with column preferences —
// show/hide + reorder (fixed columns are always shown, cannot move).
//
// props:
//   open, onClose
//   config     — { columns: [{ key, visible }] }
//   onToggle(key)
//   onMove(key, delta)
//   onReset()
//   helpers    — from makeColumnPrefs (has DEFS + FIXED_KEYS)
//   title      — dialog heading (optional)
//   testidPrefix — for data-testid uniqueness across pages
export default function TableColumnsDialog({
  open, onClose, config, onToggle, onMove, onReset, helpers, title, testidPrefix = 'table-col',
}) {
  const cols = config.columns;
  const DEF_BY_KEY = Object.fromEntries(helpers.DEFS.map((d) => [d.key, d]));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader><DialogTitle>{title || '列の表示・並び替え'}</DialogTitle></DialogHeader>

        <p className="text-xs text-zinc-500">表示する列にチェックを入れ、矢印で並び替えます。（固定）は常時表示で並び替えられません。</p>
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
          {cols.map((c, idx) => {
            const def = DEF_BY_KEY[c.key];
            if (!def) return null;
            return (
              <li key={c.key} className="flex items-center gap-2 px-3 py-2" data-testid={`${testidPrefix}-row-${c.key}`}>
                <Checkbox
                  checked={c.visible}
                  disabled={!!def.fixed}
                  onCheckedChange={() => onToggle(c.key)}
                  data-testid={`${testidPrefix}-toggle-${c.key}`}
                />
                <span className={`flex-1 text-sm ${c.visible ? 'text-zinc-800' : 'text-zinc-400'}`}>
                  {def.label}{def.fixed && <span className="ml-1.5 text-[10px] text-zinc-400">（固定）</span>}
                </span>
                <button type="button" disabled={idx === 0} onClick={() => onMove(c.key, -1)}
                  className="p-1 rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30" data-testid={`${testidPrefix}-up-${c.key}`}>
                  <ArrowUp size={15} />
                </button>
                <button type="button" disabled={idx === cols.length - 1} onClick={() => onMove(c.key, 1)}
                  className="p-1 rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30" data-testid={`${testidPrefix}-down-${c.key}`}>
                  <ArrowDown size={15} />
                </button>
              </li>
            );
          })}
        </ul>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="outline" onClick={onReset} data-testid={`${testidPrefix}-reset-btn`} className="text-zinc-600">
            <ArrowCounterClockwise size={15} className="mr-1.5" /> 初期状態
          </Button>
          <Button onClick={onClose} data-testid={`${testidPrefix}-done-btn`} className="bg-teal-700 hover:bg-teal-800">完了</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
